import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flag, TrendingUp, KeyRound } from "lucide-react";
import { SectionHead } from "../Reveal";
import { ROADMAPS } from "../../data/site";

const ACCENTS = {
  sky: { active: "bg-sky-500/15 border-sky-400 text-sky-300", dot: "bg-sky-400", ring: "border-sky-500/30", text: "text-sky-400", chip: "bg-sky-500/10 border-sky-500/30 text-sky-300" },
  blue: { active: "bg-blue-500/15 border-blue-400 text-blue-300", dot: "bg-blue-400", ring: "border-blue-500/30", text: "text-blue-400", chip: "bg-blue-500/10 border-blue-500/30 text-blue-300" },
  orange: { active: "bg-orange-500/15 border-orange-400 text-orange-300", dot: "bg-orange-400", ring: "border-orange-500/30", text: "text-orange-400", chip: "bg-orange-500/10 border-orange-500/30 text-orange-300" },
  emerald: { active: "bg-emerald-500/15 border-emerald-400 text-emerald-300", dot: "bg-emerald-400", ring: "border-emerald-500/30", text: "text-emerald-400", chip: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
};

export const Roadmaps = () => {
  const [active, setActive] = useState("network");
  const rm = ROADMAPS.find((r) => r.id === active);
  const a = ACCENTS[rm.accent];

  return (
    <section id="roadmaps" data-testid="roadmaps-section" className="relative py-20 sm:py-28 bg-[#04070F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Career Roadmaps"
          title={
            <>
              Your Step-by-Step Path from <span className="text-gradient-sky">Zero</span> to{" "}
              <span className="text-gradient-orange">Hired</span>
            </>
          }
          sub="Pick a track. Foundation → Intermediate → Expert — with the exact courses, keywords and salary band at every step."
        />

        <div className="flex flex-nowrap sm:flex-wrap justify-start sm:justify-center gap-3 mb-14 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {ROADMAPS.map((r) => (
            <button
              key={r.id}
              data-testid={`roadmap-tab-${r.id}`}
              onClick={() => setActive(r.id)}
              className={`shrink-0 whitespace-nowrap rounded-full border px-6 py-3 font-display text-sm font-semibold transition-all duration-300 ${
                active === r.id
                  ? ACCENTS[r.accent].active
                  : "border-sky-500/20 text-slate-400 hover:text-white hover:border-sky-500/40"
              }`}
            >
              {r.track.replace(" Roadmap", "")}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-3xl mx-auto"
          >
            <div className="absolute left-6 top-8 bottom-8 w-px bg-gradient-to-b from-sky-500/40 via-blue-600/40 to-orange-500/40" />
            <div className="space-y-8">
              {rm.stages.map((s, i) => (
                <motion.div
                  key={s.step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.15, duration: 0.5 }}
                  data-testid={`roadmap-stage-${active}-${i}`}
                  className="relative pl-16"
                >
                  <span className={`absolute left-3 top-7 w-7 h-7 rounded-full border-2 ${a.ring} bg-[#0B1226] flex items-center justify-center`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${a.dot}`} />
                  </span>
                  <div className={`rounded-2xl border ${a.ring} bg-[#0B1226] p-6 sm:p-7 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(56,189,248,0.12)] transition-all duration-300`}>
                    <div className="flex flex-wrap items-center gap-2.5 mb-3">
                      <span className={`font-mono2 text-[10px] uppercase tracking-[0.25em] ${a.text} font-bold`}>{s.step}</span>
                      <h3 className="font-display text-lg sm:text-xl font-bold text-white">{s.level}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {s.courses.map((c) => (
                        <span key={c} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${a.chip}`}>
                          {c}
                        </span>
                      ))}
                    </div>
                    <p className="flex items-start gap-2 text-xs text-slate-500 font-mono2 mb-3">
                      <KeyRound className="w-3.5 h-3.5 text-orange-500 shrink-0 mt-0.5" />
                      {s.keywords}
                    </p>
                    <p className="text-sm text-slate-400 leading-relaxed">{s.detail}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0F1A36] border border-sky-500/25 px-3.5 py-1.5 text-xs font-semibold text-white">
                        <Flag className="w-3.5 h-3.5 text-orange-500" /> {s.outcome}
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 px-3.5 py-1.5 text-xs font-semibold text-orange-300">
                        <TrendingUp className="w-3.5 h-3.5" /> {s.salary}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
